import DeleteIcon from '@mui/icons-material/Delete';
import { Grid, IconButton, TextField, Typography } from '@mui/material';
import { Dispatch, FunctionComponent, SetStateAction } from 'react';
import { AnswerConfig } from '../../types/GameplayTypes';
import { muiStyleSheet } from '../../util/Styles';

type Props = {
	index: number;
	answers: AnswerConfig[];
	setAnswers: Dispatch<SetStateAction<AnswerConfig[]>>;
	withFixedPoints?: AnswerConfig;
};

export const EditAnswerRow: FunctionComponent<Props> = (props) => {
	const { answers, index, withFixedPoints: fixed } = props;
	const answer = props.answers.getSafe(index);
	const pointsBefore = Number.isFinite(answer.points) ? answer.points : 0;

	const pointChange = fixed ? (fixed.points - pointsBefore) : 0;
	const willChange = pointChange !== 0;

	function changeText(value: string) {
		const newAnswers = [...answers];
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		newAnswers[index] = { ...newAnswers[index]!, text: value };
		props.setAnswers(newAnswers);
	}

	function changePoints(value: string) {
		const points = Number.parseInt(value, 10);
		const newAnswers = [...answers];
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		newAnswers[index] = { ...newAnswers[index]!, points };
		props.setAnswers(newAnswers);
	}

	function removeAnswer() {
		props.setAnswers(answers.filter((_, i) => i !== index));
	}

	return <>
		<Grid size={8}>
			<TextField label='Answer text' size="small" fullWidth
				value={answer.text}
				onChange={(e) => changeText(e.target.value)}
				slotProps={{ htmlInput: { 'data-testid': `answer-${index + 1}` } }} />
		</Grid>
		<Grid size={2}>
			<TextField label="Points" size="small"
				// Astonishingly, capitalising 'Number' here is needed to enable trimming of leading zeroes!
				type="Number" value={answer.points}
				onChange={(e) => changePoints(e.target.value)}
				slotProps={{ htmlInput: { min: 0, max: 100 } }}
				sx={[styles.pointsInput, willChange && styles.pointsWillChange]}
			/>
		</Grid>
		<Grid size={1} sx={styles.verticalCentre}>
			{willChange && <Typography variant="caption" sx={{
				color: (pointChange > 0) ? 'success.main' : 'warning.main',
				fontWeight: 'bold',
			}}>
				→ {fixed?.points} ({(pointChange > 0) ? '+' : ''}{pointChange})
			</Typography>}
		</Grid>
		<Grid size={1}>
			{(answers.length > 1) &&
				<IconButton size="small" onClick={() => removeAnswer()}>
					<DeleteIcon />
				</IconButton>}
		</Grid>
	</>;
};

const styles = muiStyleSheet({
	verticalCentre: {
		display: 'flex',
		alignItems: 'center',
	},
	pointsInput: {
		minWidth: '3rem',
	},
	pointsWillChange: {
		'& .MuiOutlinedInput-root': {
			backgroundColor: 'action.hover'
		}
	},
});